import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { MoreVertical, RotateCcw, Loader2, Bot, Hand } from "lucide-react";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const TicketActionButtons = ({ ticket }) => {
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const menuOpen = Boolean(anchorEl);
  const { user } = useContext(AuthContext);

  // Sin bot ni IA en la conexión no hay nada que tomar: el botón solo estorbaría.
  const aiAvailable = !!(
    ticket.whatsapp?.botEnabled || ticket.whatsapp?.aiEnabled
  );

  const handleUpdateTicketStatus = async (status, userId) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${ticket.id}`, { status, userId: userId || null });
      setLoading(false);
      if (status === "open") history.push(`/tickets/${ticket.id}`);
      else history.push("/tickets");
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  // Silencia (o devuelve) al bot/IA en este ticket. No toca estado ni dueño a
  // propósito: de eso ya se encargan Aceptar/Devolver/Resolver, y mezclarlo haría
  // que un simple "callar a la IA" reasignara el ticket sin pedirlo.
  const handleToggleAi = async (paused) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${ticket.id}`, { aiPaused: paused });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const Btn = ({ onClick, variant = "outline", children }) => {
    const base = "flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50";
    const variants = {
      outline: "border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800",
      primary: "bg-indigo-600 text-white hover:bg-indigo-700",
    };
    return (
      <button className={`${base} ${variants[variant]}`} onClick={onClick} disabled={loading}>
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
        {children}
      </button>
    );
  };

  return (
    <div className="flex items-center gap-1.5 ml-auto flex-shrink-0 px-2">
      {ticket.status !== "closed" && aiAvailable && (
        ticket.aiPaused ? (
          <Btn onClick={() => handleToggleAi(false)}>
            <Bot className="w-3 h-3" />
            {i18n.t("messagesList.header.buttons.resumeAi")}
          </Btn>
        ) : (
          <Btn onClick={() => handleToggleAi(true)}>
            <Hand className="w-3 h-3" />
            {i18n.t("messagesList.header.buttons.takeOver")}
          </Btn>
        )
      )}
      {ticket.status === "closed" && (
        <Btn onClick={() => handleUpdateTicketStatus("open", user?.id)}>
          <RotateCcw className="w-3 h-3" />
          {i18n.t("messagesList.header.buttons.reopen")}
        </Btn>
      )}
      {ticket.status === "open" && (
        <>
          <Btn onClick={() => handleUpdateTicketStatus("pending", null)}>
            <RotateCcw className="w-3 h-3" />
            {i18n.t("messagesList.header.buttons.return")}
          </Btn>
          <Btn variant="primary" onClick={() => handleUpdateTicketStatus("closed", user?.id)}>
            {i18n.t("messagesList.header.buttons.resolve")}
          </Btn>
          <button
            onClick={(e) => setAnchorEl(e.currentTarget)}
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          <TicketOptionsMenu
            ticket={ticket}
            anchorEl={anchorEl}
            menuOpen={menuOpen}
            handleClose={() => setAnchorEl(null)}
          />
        </>
      )}
      {ticket.status === "pending" && (
        <Btn variant="primary" onClick={() => handleUpdateTicketStatus("open", user?.id)}>
          {i18n.t("messagesList.header.buttons.accept")}
        </Btn>
      )}
    </div>
  );
};

export default TicketActionButtons;
